import { useServices } from '../context/WebsiteContext';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, ShoppingBag, Check } from 'lucide-react';
import { useSmartSite } from '../context/SmartSiteContext';

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useReveal(threshold = 0.12, deps = []) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    [el, ...el.querySelectorAll('.reveal,.reveal-left,.reveal-right,.stagger-grid')].forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, [threshold, ...deps]);
  return ref;
}

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const smart = useSmartSite();
  const services = useServices();
  const service = services.find(s => s.slug === slug);
  const [relatedProducts, setRelatedProducts] = useState([]);

  const contentRef = useReveal(0.08);
  const processRef = useReveal(0.1);
  const productsRef = useReveal(0.08, [relatedProducts.length]);

  // Track intent on page load
  useEffect(() => {
    if (service && smart) {
      smart.track('service_click', {
        content_category: service.intentCategory,
        content_slug: service.slug,
        page: `/services/${service.slug}`,
      });
    }
  }, [slug, smart?.sessionId]);

  // Fetch related products
  useEffect(() => {
    if (!service) return;
    const fetchProducts = async () => {
      try {
        const params = new URLSearchParams();
        const category = new URL(service.button_link, window.location.origin).searchParams.get('category');
        if (category) params.set('category_slug', category);
        else { setRelatedProducts([]); return; }
        const res = await axios.get(`${API_URL}/api/products?${params.toString()}&limit=4`);
        setRelatedProducts(res.data);
      } catch { setRelatedProducts([]); }
    };
    fetchProducts();
  }, [slug, service?.button_link]);

  if (!service) return <Navigate to="/services" replace />;

  // Find adjacent services for prev/next nav
  const idx = services.findIndex(s => s.slug === slug);
  const prev = idx > 0 ? services[idx - 1] : null;
  const next = idx < services.length - 1 ? services[idx + 1] : null;

  return (
    <div className="min-h-screen bg-white" data-testid="service-detail-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1B3A5C]">
        <div className="absolute inset-0 opacity-5 pattern-dots" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="pt-8 text-sm text-blue-200/50 mb-6" data-testid="service-breadcrumb">
            <Link to="/" className="hover:text-[#F5A623] transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#F5A623] transition-colors">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{service.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center pb-20 pt-4">
            <div>
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                {service.tagline}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] mb-6 text-white">
                {service.name}
              </h1>
              <p className="text-lg text-blue-100/80 leading-relaxed mb-8">
                {service.shortDescription}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={service.button_link}
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
                  data-testid="service-quote-btn"
                >
                  {service.button_text} <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/40 text-white font-bold rounded-full hover:bg-white/10 transition-colors"
                  data-testid="service-shop-btn"
                >
                  Browse All Products
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -top-6 -left-6 w-72 h-72 bg-[#F5A623]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#4A90A4]/10 rounded-full blur-3xl" />
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 25%' }}
                  data-testid="service-hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link className="text-[#1B3A5C] underline font-semibold" to={service.button_link}>
          {service.button_text} — {service.name}
        </Link>
      </div>
      {/* Content */}
      <section ref={contentRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16 reveal">
            {/* Description */}
            <div className="lg:col-span-3">
              <div className="prose prose-lg max-w-none">
                {service.description.split('\n\n').map((p, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-6">{p}</p>
                ))}
              </div>
              {service.callout && (
                <div className="mt-8 p-6 bg-[#FDF6E9] rounded-2xl border border-[#F5A623]/15" data-testid="service-callout">
                  <p className="text-gray-900 font-semibold">
                    <span className="text-[#F5A623] mr-2">&#9733;</span>
                    {service.callout}
                  </p>
                </div>
              )}
            </div>

            {/* Features sidebar */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 sticky top-28">
                <h3 className="text-lg font-bold text-gray-900 mb-6">What's Included</h3>
                <ul className="space-y-4" data-testid="service-features">
                  {service.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#4A90A4]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#4A90A4]" />
                      </div>
                      <span className="text-gray-600 text-sm leading-relaxed">{feat}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="mt-8 w-full inline-flex items-center justify-center px-6 py-3 bg-[#4A90A4] text-white font-bold rounded-full hover:bg-[#3d7a8e] transition-colors text-sm"
                  data-testid="service-contact-btn"
                >
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      {service.process && (
        <section ref={processRef} className="py-20 bg-[#FDF6E9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 reveal">
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">How It Works</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Our Process</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
              {service.process.map((item, i) => (
                <div key={i} className="relative p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <span className="text-7xl font-extrabold text-[#F5A623]/10 absolute top-4 right-6 select-none">{item.step}</span>
                  <div className="w-14 h-14 bg-[#4A90A4] rounded-2xl flex items-center justify-center text-white font-bold text-lg mb-5">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {Array.isArray(service.gallery) && service.gallery.length > 0 && (
        <section className="py-20 bg-white" data-testid="service-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 reveal">
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">Recent Work</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">From the KYIS Studio</h2>
              <p className="mt-3 text-gray-500 max-w-2xl mx-auto">Real branded pieces shipped to clients across Maryland and beyond.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-grid reveal">
              {service.gallery.map((g, i) => (
                <figure
                  key={i}
                  className="group relative overflow-hidden rounded-2xl bg-gray-100 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow"
                  data-testid={`service-gallery-item-${i}`}
                >
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={g.src}
                      alt={g.alt || service.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {g.caption && (
                    <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/75 via-black/40 to-transparent text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {g.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
            <div className="text-center mt-12 reveal">
              <Link
                to={service.button_link}
                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1B3A5C] text-white font-bold rounded-full hover:bg-[#0F2A3D] transition-colors text-sm"
                data-testid="gallery-quote-cta"
              >
                Start a project like these
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section ref={productsRef} className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 reveal">
              <p className="text-[#4A90A4] text-sm font-semibold tracking-[0.2em] uppercase mb-4">Shop This Category</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Related Products</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 stagger-grid reveal">
              {relatedProducts.map((product, i) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`related-product-${i}`}
                >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    {product.primary_image ? (
                      <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-10 h-10 text-gray-200" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-[#4A90A4] transition-colors">{product.name}</h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-[#4A90A4]">${(product.sale_price || product.base_price).toFixed(2)}</span>
                      {product.sale_price && <span className="text-xs text-gray-400 line-through">${product.base_price.toFixed(2)}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prev / Next */}
      <section className="border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            {prev ? (
              <Link to={`/services/${prev.slug}`} className="group py-8 pr-8 flex items-center gap-4 hover:bg-gray-50 transition-colors" data-testid="prev-service">
                <ChevronRight className="w-5 h-5 text-gray-300 rotate-180 group-hover:text-[#4A90A4] transition-colors" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Previous</p>
                  <p className="font-bold text-gray-900 group-hover:text-[#4A90A4] transition-colors">{prev.name}</p>
                </div>
              </Link>
            ) : <div />}
            {next ? (
              <Link to={`/services/${next.slug}`} className="group py-8 pl-8 flex items-center justify-end gap-4 hover:bg-gray-50 transition-colors text-right" data-testid="next-service">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Next</p>
                  <p className="font-bold text-gray-900 group-hover:text-[#4A90A4] transition-colors">{next.name}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#4A90A4] transition-colors" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#4A90A4] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/5 rounded-full" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">
            Ready to Get Started with {service.name}?
          </h2>
          <p className="text-white/75 text-lg mb-10 max-w-xl mx-auto">
            Choose your items and personalize them, or build a quote for your project.
          </p>
          <Link
            to={service.button_link}
            className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
            data-testid="service-cta-quote-btn"
          >
            {service.button_text} <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
